import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import {
  useAddNotificationMutation,
  useLazyDeleteNotificationQuery,
  useLazyFetchNotificationsQuery,
} from '../api/custom-notification.api';
import {
  CustomNotificationAC,
  clearCustomNotificationState,
  setCustomNotificationModal,
  setSelectedNotification,
} from '../store/custom-notification.slice';
import {
  AddCustomNotificationPayload,
  DeleteCustomNotificationPayload,
  FetchCustomNotificationsPayload,
  ICustomNotification,
  ICustomNotificationModal,
} from '../types/custom-notification.types';

interface OpenModal {
  modal: ICustomNotificationModal;
  customNotification: ICustomNotification | null;
}

interface UseCustomNotifications {
  clearState: () => void;
  fetchNotificationsHandler: (payload: FetchCustomNotificationsPayload) => Promise<boolean>;
  openNotificationsModal: (payload: OpenModal) => void;
  closeNotificationsModal: () => void;
  deleteNotificationHandler: (payload: DeleteCustomNotificationPayload) => Promise<boolean>;
  addNotificationHandler: (payload: AddCustomNotificationPayload) => Promise<ICustomNotification | null>;
}

export const useCustomNotifications = (): UseCustomNotifications => {
  const dispatch = useAppDispatch();

  const [fetchNotificationsQuery] = useLazyFetchNotificationsQuery();
  const [addNotificationQuery] = useAddNotificationMutation();
  const [deleteNotificationQuery] = useLazyDeleteNotificationQuery();

  const fetchNotificationsHandler = async (payload: FetchCustomNotificationsPayload) => {
    dispatch(CustomNotificationAC.toggleLoading(true));
    const { data: response, error } = await fetchNotificationsQuery(payload);
    dispatch(CustomNotificationAC.toggleLoading(false));
    const isError = error || !response?.data;

    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get notifications!');
    } else {
      batch(() => {
        dispatch(CustomNotificationAC.setItems(response.data));
        dispatch(CustomNotificationAC.setPaginationMeta(response.meta));
      });
    }

    return !isError;
  };

  const addNotificationHandler = async (payload: AddCustomNotificationPayload) => {
    dispatch(CustomNotificationAC.toggleLoading(true));
    const { data: response, error } = await addNotificationQuery(payload);
    dispatch(CustomNotificationAC.toggleLoading(false));
    const isError = error || !response?.data?.id;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to add Notification !');
      return null;
    } else {
      toast.success('Notification successfully added!');
    }
    dispatch(CustomNotificationAC.addItem(response.data));
    return response.data;
  };

  const deleteNotificationHandler = async (payload: DeleteCustomNotificationPayload) => {
    dispatch(CustomNotificationAC.toggleLoading(true));
    const { error } = await deleteNotificationQuery(payload);
    dispatch(CustomNotificationAC.toggleLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to remove Bid!');
    } else {
      toast.success('Bid successfully removed!');
      dispatch(CustomNotificationAC.removeItem(payload.id));
    }

    return !error;
  };

  const openNotificationsModal = ({ modal, customNotification }: OpenModal) => {
    batch(() => {
      dispatch(setCustomNotificationModal(modal));
      dispatch(setSelectedNotification(customNotification));
    });
  };

  const closeNotificationsModal = () => {
    batch(() => {
      dispatch(setCustomNotificationModal(null));
      dispatch(setSelectedNotification(null));
    });
  };

  const clearState = () => {
    dispatch(clearCustomNotificationState());
  };

  return {
    clearState,
    fetchNotificationsHandler,
    openNotificationsModal,
    closeNotificationsModal,
    deleteNotificationHandler,
    addNotificationHandler,
  };
};
